import React, { useMemo, useEffect, useState } from 'react';
import { QuizResult, UserAnswer } from '../types';
import { Question } from '../types';
import { calculateScore } from '../utils/score';
import apiClient from '../auth/service';

interface ResultsScreenProps {
    results: QuizResult;
    onRestart: () => void;
    onReview: () => void;
    passingScore?: number;
    displayLanguage: 'pt' | 'en' | 'es';
}

const ResultsScreen: React.FC<ResultsScreenProps> = ({ results, onRestart, onReview, passingScore, displayLanguage }) => {
    const [dynamicPassingScore, setDynamicPassingScore] = useState<number | undefined>(undefined);
    useEffect(() => {
        const needFetch = !(typeof passingScore === 'number' && passingScore > 0) && !(typeof results.passingScore === 'number' && results.passingScore > 0);
        if (!needFetch) return;
        (async () => {
            try {
                const sim = await apiClient.get('/simulados/active');
                const ps = Number(sim?.passingScore) || undefined;
                setDynamicPassingScore(ps);
            } catch {}
        })();
    }, [passingScore, results]);

    const score = useMemo(() => calculateScore(results), [results]);
    const passing = (typeof passingScore === 'number' && passingScore > 0)
        ? passingScore
        : (typeof results.passingScore === 'number' && results.passingScore > 0)
            ? results.passingScore
            : (typeof dynamicPassingScore === 'number' && dynamicPassingScore > 0 ? dynamicPassingScore : 720);
    const didPass = score >= passing;

    const correctAnswers = Array.from(results.answers.values()).filter((a: UserAnswer) => a.isCorrect).length;
    const totalQuestions = results.questions.length;

    const minutes = Math.floor(results.timeTaken / 60);
    const seconds = results.timeTaken % 60;

    const domainStats = useMemo(() => {
        const stats: { [domain: string]: { correct: number; total: number } } = {};
        let hasDomains = false;

        results.questions.forEach((q: Question) => {
            const domain = q.domain || (displayLanguage === 'en' ? 'Uncategorized' : displayLanguage === 'es' ? 'Sin categoría' : 'Não categorizado');
            if(q.domain) hasDomains = true;

            if (!stats[domain]) {
                stats[domain] = { correct: 0, total: 0 };
            }
            stats[domain].total++;
            const userAnswer = results.answers.get(q.id);
            if (userAnswer?.isCorrect) {
                stats[domain].correct++;
            }
        });
        return hasDomains ? stats : null;
    }, [results, displayLanguage]);

    const formatTime = (totalSeconds: number) => {
        if (isNaN(totalSeconds) || totalSeconds < 0) return '00:00';
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    };

    const timeAnalysis = useMemo(() => {
        if (!results.timePerQuestion || results.timePerQuestion.size === 0) {
            return null;
        }

        const domainTimes: { [domain: string]: { totalTime: number; count: number } } = {};
        
        results.questions.forEach(q => {
            const domain = q.domain || (displayLanguage === 'en' ? 'Uncategorized' : displayLanguage === 'es' ? 'Sin categoría' : 'Não categorizado');
            if (!domainTimes[domain]) {
                domainTimes[domain] = { totalTime: 0, count: 0 };
            }
            const timeSpent = results.timePerQuestion.get(q.id) || 0;
            domainTimes[domain].totalTime += timeSpent;
            domainTimes[domain].count++;
        });

        const slowestQuestions = Array.from(results.timePerQuestion.entries())
            .sort(([, timeA], [, timeB]) => timeB - timeA)
            .slice(0, 5)
            .map(([questionId, time]) => {
                const questionIndex = results.questions.findIndex(q => q.id === questionId);
                return {
                    number: questionIndex + 1,
                    time: time
                };
            });

        return { domainTimes, slowestQuestions };

    }, [results, displayLanguage]);


    return (
        <div className="bg-white dark:bg-slate-800 p-8 rounded-lg shadow-2xl text-center flex flex-col items-center animate-fade-in w-full">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{displayLanguage === 'en' ? 'Exam Finished!' : displayLanguage === 'es' ? '¡Examen finalizado!' : 'Exame Finalizado!'}</h2>

            <div className={`text-6xl font-bold my-6 ${didPass ? 'text-green-400' : 'text-red-400'}`}>
                {score}
            </div>
            
            <div className={`text-2xl font-semibold mb-4 ${didPass ? 'text-green-400' : 'text-red-400'}`}>
                {didPass
                    ? (displayLanguage === 'en' ? 'Congratulations! You passed.' : displayLanguage === 'es' ? '¡Felicidades! Aprobaste.' : 'Parabéns! Você foi aprovado.')
                    : (displayLanguage === 'en' ? 'You did not pass. Keep studying!' : displayLanguage === 'es' ? 'No aprobaste. ¡Sigue estudiando!' : 'Você não foi aprovado. Continue estudando!')}
            </div>

            <div className="text-gray-600 dark:text-slate-400 text-lg mb-8">
                {displayLanguage === 'en' ? 'Passing score' : displayLanguage === 'es' ? 'Puntuación para aprobar' : 'Pontuação para aprovação'}: <span className="font-bold">{passing}</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-lg w-full max-w-md mb-8">
                 <div className="bg-gray-100 dark:bg-slate-700 p-4 rounded-lg">
                    <div className="font-bold text-2xl text-green-400">{correctAnswers}</div>
                    <div className="text-gray-600 dark:text-slate-400">{displayLanguage === 'en' ? 'Correct' : displayLanguage === 'es' ? 'Correctas' : 'Corretas'}</div>
                </div>
                 <div className="bg-gray-100 dark:bg-slate-700 p-4 rounded-lg">
                    <div className="font-bold text-2xl text-red-400">{totalQuestions - correctAnswers}</div>
                    <div className="text-gray-600 dark:text-slate-400">{displayLanguage === 'en' ? 'Incorrect' : displayLanguage === 'es' ? 'Incorrectas' : 'Incorretas'}</div>
                </div>
                 <div className="bg-gray-100 dark:bg-slate-700 p-4 rounded-lg">
                    <div className="font-bold text-2xl text-orange-400">{totalQuestions}</div>
                    <div className="text-gray-600 dark:text-slate-400">Total</div>
                </div>
            </div>
             <div className="text-gray-600 dark:text-slate-400 mb-8">
                {displayLanguage === 'en' ? 'Time Used' : displayLanguage === 'es' ? 'Tiempo usado' : 'Tempo Utilizado'}: {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
            </div>
            
            {domainStats && (
                <div className="w-full max-w-2xl bg-gray-50 dark:bg-slate-900/50 p-6 rounded-lg mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{displayLanguage === 'en' ? 'Performance by Domain' : displayLanguage === 'es' ? 'Rendimiento por dominio' : 'Desempenho por Domínio'}</h3>
                    <div className="space-y-4">
                        {Object.entries(domainStats).map(([domain, stats]) => {
                            const { correct, total } = stats as { correct: number; total: number };
                            const percentage = total > 0 ? Math.round((correct / total) * 100) : 0;
                            const barColor = percentage >= 72 ? 'bg-green-500' : 'bg-red-500';
                            return (
                                <div key={domain}>
                                    <div className="flex justify-between items-center mb-1 text-gray-700 dark:text-slate-300">
                                        <span>{domain}</span>
                                        <span>{correct}/{total} ({percentage}%)</span>
                                    </div>
                                    <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2.5">
                                        <div className={barColor + " h-2.5 rounded-full"} style={{ width: `${percentage}%` }}></div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            
            {timeAnalysis && (
                <div className="w-full max-w-2xl bg-gray-50 dark:bg-slate-900/50 p-6 rounded-lg mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{displayLanguage === 'en' ? 'Time Analysis' : displayLanguage === 'es' ? 'Análisis de tiempo' : 'Análise de Tempo'}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                        <div>
                            <h4 className="text-lg font-semibold text-orange-400 mb-3 text-left">{displayLanguage === 'en' ? 'Average Time by Domain' : displayLanguage === 'es' ? 'Tiempo medio por dominio' : 'Tempo Médio por Domínio'}</h4>
                            <ul className="space-y-2 text-left">
                                {Object.entries(timeAnalysis.domainTimes).map(([domain, data]) => {
                                    // FIX: Add type assertion to correctly type 'data' from Object.entries.
                                    const { totalTime, count } = data as { totalTime: number; count: number };
                                    const avgTime = count > 0 ? Math.round(totalTime / count) : 0;
                                    return (
                                        <li key={domain} className="flex justify-between text-gray-700 dark:text-slate-300 border-b border-gray-200 dark:border-slate-700 py-1">
                                            <span>{domain}</span>
                                            <span className="font-mono">{formatTime(avgTime)}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-lg font-semibold text-orange-400 mb-3 text-left">{displayLanguage === 'en' ? 'Slowest Questions' : displayLanguage === 'es' ? 'Preguntas más lentas' : 'Questões Mais Lentas'}</h4>
                            <ul className="space-y-2 text-left">
                                {timeAnalysis.slowestQuestions.map(q => (
                                    <li key={q.number} className="flex justify-between text-gray-700 dark:text-slate-300 border-b border-gray-200 dark:border-slate-700 py-1">
                                        <span>{displayLanguage === 'en' ? 'Question' : displayLanguage === 'es' ? 'Pregunta' : 'Questão'} {q.number}</span>
                                        <span className="font-mono">{formatTime(q.time)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}


            <div className="flex flex-col sm:flex-row gap-4 mt-4">
                <button
                    onClick={onRestart}
                    className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-8 rounded-lg transition-transform duration-200 transform hover:scale-105 shadow-lg"
                >
                    <i className="fas fa-redo mr-2"></i>
                    {displayLanguage === 'en' ? 'Try Again' : displayLanguage === 'es' ? 'Intentar de nuevo' : 'Tentar Novamente'}
                </button>
                <button
                    onClick={onReview}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg transition-transform duration-200 transform hover:scale-105 shadow-lg"
                >
                    <i className="fas fa-search mr-2"></i>
                    {displayLanguage === 'en' ? 'Review Answers' : displayLanguage === 'es' ? 'Revisar respuestas' : 'Revisar Respostas'}
                </button>
            </div>
        </div>
    );
};

export default ResultsScreen;
